import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import FormUnavailable from "../../../components/FormUnavailable";
import FullPageLoader from "../../../components/FullPageLoader";
import { app, db } from "../../../firebase";
import { getFormByRoute } from "../../../services/formService";
import { getDefaultTheme, getThemeById as getThemeByIdSvc } from "../../../services/themeService";
import { isSubscriptionAccessAllowed } from "../../../utils/subscriptionAccess";
import FormPreview from "./FormPreview";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 20;

/**
 * Public document submission form viewer.
 * URL: /:businessSlug/:formSlug/:leadId
 * Loads the document form, validates the lead exists, and on submit uploads files via Cloud Function.
 */
const DocumentFormViewer = () => {
    const { businessSlug, formSlug, leadId } = useParams();
    const [form, setForm] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(true);
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [theme, setTheme] = useState(null);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);
    const [blockedBusinessName, setBlockedBusinessName] = useState(null);
    const submitInProgressRef = useRef(false);

    useEffect(() => {
        const fetchFormAndLead = async () => {
            const reserved = ["signin", "signup", "dashboard", "form", "profile", "settings", "home", "www"];
            if (!businessSlug || !formSlug || !leadId ||
                reserved.includes(businessSlug.toLowerCase()) ||
                reserved.includes(formSlug.toLowerCase())) {
                setError("Invalid link.");
                setLoading(false);
                return;
            }

            try {
                let formDataFromDoc = null;
                let formDocId = null;
                const routed = await getFormByRoute(businessSlug, formSlug);
                if (routed) {
                    formDocId = routed.id;
                    formDataFromDoc = routed;
                } else {
                    const formsQuery = query(
                        collection(db, "forms"),
                        where("businessSlug", "==", businessSlug),
                        where("formSlug", "==", formSlug)
                    );
                    const formsSnap = await getDocs(formsQuery);
                    if (formsSnap.empty) {
                        setError("Form not found.");
                        setLoading(false);
                        return;
                    }

                    const formDoc = formsSnap.docs[0];
                    formDataFromDoc = formDoc.data();
                    formDocId = formDoc.id;
                }

                if (formDataFromDoc.formType !== "document") {
                    setError("This link is for a document request form. The form type does not match.");
                    setLoading(false);
                    return;
                }
                if (formDataFromDoc.status !== "published") {
                    setError("This document request form is not published.");
                    setLoading(false);
                    return;
                }

                const businessId = formDataFromDoc.businessId;

                // Business subscription gate (does not change form status)
                try {
                    const businessSnap = await getDoc(doc(db, "businesses", businessId));
                    if (!businessSnap.exists()) {
                        setError("Business not found.");
                        setLoading(false);
                        return;
                    }
                    const businessData = businessSnap.data();
                    if (!isSubscriptionAccessAllowed(businessData)) {
                        setBlockedBusinessName(businessData.businessName || null);
                        setSubscriptionBlocked(true);
                        setLoading(false);
                        return;
                    }
                } catch (subErr) {
                    console.error("Subscription check failed:", subErr);
                    setError("Something went wrong. Please try again later.");
                    setLoading(false);
                    return;
                }

                // Lead existence is validated by the Cloud Function on submit (client cannot read leads - auth required).

                if (formDataFromDoc.themeId && businessId) {
                    try {
                        const t = await getThemeByIdSvc(businessId, formDataFromDoc.themeId);
                        if (t) {
                            setTheme({
                                ...t,
                                logoUrl: t.logo,
                                backgroundImageUrl: t.backgroundImage
                            });
                        }
                    } catch (themeErr) {
                        console.warn("Theme load failed:", themeErr);
                    }
                }

                setForm({
                    ...formDataFromDoc,
                    id: formDocId,
                    businessId
                });
                setError(null);
            } catch (err) {
                console.error("Document form viewer error:", err);
                setError("Something went wrong. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchFormAndLead();
    }, [businessSlug, formSlug, leadId]);

    const handleChange = (key, value) => {
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const fileToBase64 = (file) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
        });

    const handleSubmit = async (data) => {
        if (!form?.businessId || !form?.id) return;
        if (submitInProgressRef.current) return;
        submitInProgressRef.current = true;
        setSubmitting(true);
        setValidationErrors([]);

        const steps = form.steps || [];
        const uploadStepEntries = steps
            .map((step, index) => ({ step, index }))
            .filter(({ step }) => step.type === "file" || step.type === "image");
        const stepKey = (step, index) => `${step.label}__${index}`;
        const hasFilesForStep = (step, index) => {
            const val = data[stepKey(step, index)];
            if (!val) return false;
            const list = Array.isArray(val) ? val : [val];
            return list.some(f => f instanceof File);
        };
        const allComplete = uploadStepEntries.length > 0 && uploadStepEntries.every(({ step, index }) => hasFilesForStep(step, index));
        if (!allComplete) {
            toast.error("Please add at least one file to each step before submitting.");
            submitInProgressRef.current = false;
            setSubmitting(false);
            return;
        }

        const allFiles = [];
        for (const { step, index } of uploadStepEntries) {
            const value = data[stepKey(step, index)];
            if (!value) continue;
            const fileList = Array.isArray(value) ? value : [value];
            for (const file of fileList) {
                if (file instanceof File) allFiles.push(file);
            }
        }

        const errors = [];
        if (allFiles.length === 0) {
            errors.push("Please add at least one file before submitting.");
        }
        if (allFiles.length > MAX_FILES) {
            errors.push(`Maximum ${MAX_FILES} files allowed. You have ${allFiles.length}.`);
        }
        const oversized = allFiles.filter(f => f.size > MAX_FILE_SIZE_BYTES);
        if (oversized.length > 0) {
            const names = oversized.map(f => f.name).slice(0, 3).join(", ");
            const more = oversized.length > 3 ? ` and ${oversized.length - 3} more` : "";
            errors.push(`These files exceed 10MB and must be removed or replaced: ${names}${more}.`);
        }
        if (errors.length > 0) {
            setValidationErrors(errors);
            submitInProgressRef.current = false;
            setSubmitting(false);
            return;
        }

        const filesToSend = [];
        for (const file of allFiles) {
            try {
                const base64 = await fileToBase64(file);
                filesToSend.push({ name: file.name, type: file.type, base64 });
            } catch (e) {
                console.error("File to base64 failed:", e);
                toast.error(`Could not read "${file.name}". Try again or choose another file.`);
            }
        }
        if (filesToSend.length === 0) {
            setValidationErrors(["No files could be read. Please try again."]);
            submitInProgressRef.current = false;
            setSubmitting(false);
            return;
        }

        try {
            const functions = getFunctions(app);
            const submitDocumentForm = httpsCallable(functions, "submitDocumentForm");
            await submitDocumentForm({
                businessId: form.businessId,
                leadId,
                formId: form.id,
                formName: form.formName || "Document request",
                files: filesToSend
            });
            setSubmitted(true);
        } catch (err) {
            console.error("Submit document form failed:", err);
            toast.error(err.message || "Upload failed. Please try again.");
        } finally {
            setSubmitting(false);
            submitInProgressRef.current = false;
        }
    };

    const appliedTheme = theme || getDefaultTheme();
    const pageStyle = {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        fontFamily: appliedTheme.fontFamily || "sans-serif",
        background: appliedTheme.backgroundColor || "#f5f7fa",
        backgroundImage: appliedTheme.backgroundImageUrl ? `url(${appliedTheme.backgroundImageUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
    };
    const cardStyle = {
        maxWidth: "420px",
        width: "100%",
        padding: "2rem",
        borderRadius: appliedTheme.buttonBorderRadius || "12px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        backgroundColor: "#fff",
        textAlign: "center",
    };

    if (loading) return <FullPageLoader text="Loading..." />;
    if (subscriptionBlocked) {
        return <FormUnavailable businessName={blockedBusinessName} />;
    }
    if (error) {
        return (
            <div style={pageStyle}>
                <div style={cardStyle}>
                    <h2 style={{ marginBottom: "1rem", color: "#1f2937" }}>Unable to load form</h2>
                    <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>{error}</p>
                    <p style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Please check the link or try again later.</p>
                </div>
            </div>
        );
    }
    if (submitting) return <FullPageLoader text="Uploading documents..." />;
    if (submitted) {
        const confirmationStyle = {
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: appliedTheme.backgroundImageUrl
                ? `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${appliedTheme.backgroundImageUrl})`
                : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            fontFamily: appliedTheme.fontFamily || "system-ui, -apple-system, sans-serif",
        };
        const thankYouCardStyle = {
            backgroundColor: "white",
            borderRadius: "16px",
            padding: "3rem",
            maxWidth: "600px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
        };
        const logoStyle = {
            maxWidth: "200px",
            maxHeight: "80px",
            marginBottom: "2rem",
            objectFit: "contain",
        };
        const titleStyle = {
            fontSize: "2.5rem",
            fontWeight: "700",
            color: appliedTheme.buttonColor || appliedTheme.headingColor || "#1f2937",
            marginBottom: "1rem",
            lineHeight: "1.2",
        };
        const subtitleStyle = {
            fontSize: "1.25rem",
            color: "#6b7280",
            marginBottom: "2rem",
            lineHeight: "1.5",
        };
        const messageStyle = {
            fontSize: "1.1rem",
            color: "#374151",
            marginBottom: "2rem",
            lineHeight: "1.6",
        };
        return (
            <div style={confirmationStyle}>
                <div style={thankYouCardStyle}>
                    {appliedTheme.logoUrl && (
                        <img src={appliedTheme.logoUrl} alt="Company Logo" style={logoStyle} />
                    )}
                    <h1 style={titleStyle}>Thank You!</h1>
                    <h2 style={subtitleStyle}>Your documents have been uploaded successfully</h2>
                    <p style={messageStyle}>
                        We've received your documents and will get back to you if we need anything else.
                        Thank you for submitting your documents.
                    </p>
                    <div style={{ marginTop: "2rem", fontSize: "0.9rem", color: "#9ca3af" }}>
                        <p>You can safely close this tab or window.</p>
                    </div>
                </div>
            </div>
        );
    }
    if (!form?.steps?.length) {
        return (
            <div style={pageStyle}>
                <div style={cardStyle}>
                    <p style={{ color: "#6b7280" }}>This form has no upload fields.</p>
                </div>
            </div>
        );
    }

    const steps = form.steps || [];
    // Attach stable _stepKey per file/image step so FormPreview uses correct keys regardless of render order
    const stepsWithKeys = steps.map((step, i) => ({
        ...step,
        _stepKey: (step.type === "file" || step.type === "image") ? `${step.label}__${i}` : undefined,
    }));
    const uploadStepEntries = steps
        .map((step, index) => ({ step, index }))
        .filter(({ step }) => step.type === "file" || step.type === "image");
    const stepKey = (step, index) => `${step.label}__${index}`;
    const hasFilesForStep = (step, index) => {
        const val = formData[stepKey(step, index)];
        if (!val) return false;
        const list = Array.isArray(val) ? val : [val];
        return list.some(f => f instanceof File);
    };
    const allStepsComplete = uploadStepEntries.length > 0 && uploadStepEntries.every(({ step, index }) => hasFilesForStep(step, index));

    return (
        <>
            {validationErrors.length > 0 && (
                <div style={{ padding: "1rem 2rem", maxWidth: 800, margin: "0 auto" }}>
                    <Alert variant="danger" dismissible onClose={() => setValidationErrors([])}>
                        <Alert.Heading>Please fix the following:</Alert.Heading>
                        <ul className="mb-0">
                            {validationErrors.map((msg, i) => (
                                <li key={i}>{msg}</li>
                            ))}
                        </ul>
                    </Alert>
                </div>
            )}
            <FormPreview
                steps={stepsWithKeys}
                formData={formData}
                onChange={handleChange}
                inventoryItems={[]}
                formMode="published"
                theme={theme || getDefaultTheme()}
                onSubmit={handleSubmit}
                showSteps={form.showSteps || false}
                stepIndicatorStyle={form.stepIndicatorStyle || "progress"}
                formStyle={form.formStyle || "typeform"}
                submitting={submitting}
                submitButtonText="Upload documents"
                submitDisabled={!allStepsComplete}
                useStepIndexKey={true}
            />
        </>
    );
};

export default DocumentFormViewer;
